// src/modules/IntegratedMessaging/components/display/CardList.tsx
import { SectionBox } from '@shared/components/ui/SectionBox';
import { Card } from './Card';
import { IntegratedMessaging } from '../../models/IntegratedMessaging';
import { IntegratedMessagingApiAdapter } from '../../adapters/IntegratedMessagingApiAdapter';
import { createHttpClient } from '@adapters/HttpClient';
import { apiClientWH } from '@shared/services';
import { useToast } from '@shared/hooks/use-toast';
import { useTranslation } from 'react-i18next';
import { useRef, useCallback } from 'react';
import { EditIntegrationModalRef } from '../modals/EditIntegrationModal';
import { ConfirmDeactivateModal, ConfirmDeactivateModalRef } from '../modals/ConfirmDeactivateModal';

interface Props {
  data: IntegratedMessaging[];
  refetch: () => void;
  modalRef: React.RefObject<EditIntegrationModalRef>;
}

const httpClient = createHttpClient(apiClientWH);
const adapter = new IntegratedMessagingApiAdapter(httpClient);

export const CardList = ({ data, refetch, modalRef }: Props) => {
  const { t } = useTranslation('integratedmessagingModule');
  const { toast } = useToast();
  const deactivateModalRef = useRef<ConfirmDeactivateModalRef>(null);

  const performDeactivation = useCallback(
    async (id: number) => {
      try {
        await adapter.deactivateIntegration(id);
        toast({ title: t('messages.deactivated') });
        refetch();
      } catch (err: any) {
        toast({
          title: t('messages.deactivate_error'),
          description: err?.response?.data?.message || err?.message || 'Error desconocido',
          variant: 'destructive',
        });
      }
    },
    [toast, t, refetch],
  );

  const performActivation = useCallback(
    async (id: number) => {
      try {
        await adapter.activateIntegration(id);
        toast({ title: t('messages.activated') });
        refetch();
      } catch (err: any) {
        toast({
          title: t('messages.activate_error'),
          description: err?.response?.data?.message || err?.message || 'Error desconocido',
          variant: 'destructive',
        });
      }
    },
    [toast, t, refetch],
  );

  const handleToggleActive = useCallback(
    (id: number, isActive: boolean) => {
      if (isActive) {
        // Si está activa, mostrar modal de confirmación antes de desactivar
        const integration = data.find((item) => item.id === id);
        if (integration) {
          deactivateModalRef.current?.open(integration.name, () => {
            performDeactivation(id);
          });
        }
      } else {
        // Si está inactiva, activar directamente
        performActivation(id);
      }
    },
    [data, performDeactivation, performActivation],
  );

  const handleEdit = (integration: IntegratedMessaging) => {
    modalRef.current?.open(integration);
  };

  if (!Array.isArray(data)) return null;

  return (
    <>
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        {data.map((item) => (
          <SectionBox key={item.id}>
            <Card
              id={item.id}
              name={item.name}
              logoUrl={item.courier.image_url}
              isActive={item.is_active}
              onToggleActive={handleToggleActive}
              onEdit={() => handleEdit(item)}
            />
          </SectionBox>
        ))}
      </div>

      <ConfirmDeactivateModal ref={deactivateModalRef} />
    </>
  );
};
